import { Box } from '@mui/material';
import {
  Button,
  FormErrors,
  Modal,
  ModalProps,
  TextInput,
} from 'components/ui';
import { AddEmailFormData, addEmailFormSchema } from 'forms';
import { useAppForm, useProfile } from 'hooks';
import React, { ReactElement, useEffect, useState } from 'react';
import { useProfileApi } from 'services/api';
import { throttle } from 'lodash';
import { useSnackbar } from 'notistack';
import { Link } from 'components/lib';

export interface AddEmailModalProps
  extends Required<Pick<ModalProps, 'open' | 'onClose'>> {}

export const AddEmailModal = ({
  open,
  onClose,
}: AddEmailModalProps): ReactElement => {
  const { profile } = useProfile({ revalidateOnMount: true });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, touchedFields: touched },
  } = useAppForm(addEmailFormSchema);

  const [isSubmitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) setSubmitting(false);

    if (!profile) return;

    reset({ email: '' });
  }, [profile, open]);

  const profileApi = useProfileApi();

  const { enqueueSnackbar } = useSnackbar();

  const onSubmit = throttle(
    async (data: AddEmailFormData) => {
      setSubmitting(true);
      const result = await profileApi.addEmail(data.email);

      if (result.success) {
        enqueueSnackbar(`Please check your email ${data.email} to confirm`, {
          variant: 'success',
        });
        return onClose?.();
      }

      setSubmitting(false);
      enqueueSnackbar(result.error.message, { variant: 'error' });
    },
    200,
    { leading: true },
  );

  return (
    <Modal title="Add An Email Address" open={open} onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          style={{ width: 600, maxWidth: '80vw' }}
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <Box width={300}>
            <TextInput
              label="Email Address"
              hasError={touched.email && !!errors.email}
              {...register('email')}
            />
            <FormErrors>{errors.email?.message}</FormErrors>
          </Box>

          {/*
          <Box>
            <Typography>
              We will send an email to this address with a confirmation link.
            </Typography>
          </Box>
  */}
          <Box mt={10} display="flex">
            <Button
              minWidth={300}
              type="submit"
              variant="vibrant"
              disabled={isSubmitting}
            >
              Send Confirmation Email
            </Button>
          </Box>
          <Box mt={30}>
            <Link onClick={() => onClose()}>Cancel</Link>
          </Box>
        </Box>
      </form>
    </Modal>
  );
};
